import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { Link } from 'libs/router';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import OverdueDeliverablesPopover from '../../overdue-deliverables/popovers/OverdueDeliverablesPopover';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerActionRequired, isNegotiationsReopened, isQuote, isQuoteProvided } from 'utils/subcampaign';

import './subcampaign-banners.scss';

applyTranslations(require('./subcampaign-banners.i18n.json'));

import type {
  Campaign,
  DeliverablesOverdueBySubcampaignIdLocal,
  DeliverablesOverdueLocal,
  Subcampaign
} from 'types';

type Props = {
  hasCampaignLinks: boolean,
  overdueDeliverables?: DeliverablesOverdueBySubcampaignIdLocal,
  subcampaign: Subcampaign,
  campaign: Campaign
};

const MSG_KEY = 'subcampaign_banners';

class SubcampaignBanners extends React.Component<Props> {
  private getRoute() {
    const { subcampaign, campaign } = this.props;

    return `/campaigns/${String(campaign.id)}/details/${String(subcampaign.id)}`;
  }

  private getOverdueDeliverables(): DeliverablesOverdueLocal | null {
    const { overdueDeliverables, subcampaign } = this.props;

    if (isNil(overdueDeliverables)
      || isEmpty(overdueDeliverables)
      || !subcampaign.id
      || isNil(overdueDeliverables[subcampaign.id])
    ) {
      return null;
    }

    const influencerOverdueDeliverables = overdueDeliverables[subcampaign.id]?.deliverables
      .filter(deliverable => (
        deliverable.contentGoLiveTimeline.status === 'overdue'
          || deliverable.insightsProvisionTimeline.status === 'overdue'
      ));

    return influencerOverdueDeliverables.length ? {
      deliverables: influencerOverdueDeliverables,
      listById: influencerOverdueDeliverables.map(deliverable => deliverable.id),
      subcampaignId: subcampaign.id
    } : null;
  }

  renderIcon = (color) => (
    <div className="subcampaign-banners__icon">
      <SVGDynamicIcon
        name="exclamation-inside-circle-white-bg-custom-fg"
        width={16}
        height={16}
        customColor={color}
      />
    </div>
  );

  private renderLink(linkTo: string, child: React.ReactNode): React.ReactNode {
    return (
      <Link
        to={linkTo}
        className="subcampaign-banners__link"
      >
        {child}
      </Link>
    );
  }

  private renderPopover(linkTo: string, child: React.ReactNode) {
    return (
      <OverdueDeliverablesPopover
        overdueDeliverables={this.getOverdueDeliverables()}
        triggerComponent={this.renderLink(linkTo, child)}
      />
    );
  }

  private renderBanner(linkTo: string, child: React.ReactNode, type: 'offer' | 'overdue'): React.ReactNode {
    if (this.props.hasCampaignLinks) {
      return type === 'overdue'
        ? this.renderPopover(linkTo, child)
        : this.renderLink(linkTo, child);
    }

    return (
      <Tooltip
        message={__t(`${MSG_KEY}.not_available`)}
        classNameWrapper="subcampaign-banners__link"
      >
        {child}
      </Tooltip>
    );
  }

  renderOverdueBanner(): React.ReactNode {
    const updatedOverdueDeliverables = this.getOverdueDeliverables();

    if (isNil(updatedOverdueDeliverables)
      || isEmpty(updatedOverdueDeliverables)
      || !(updatedOverdueDeliverables?.listById || []).length) {
      return null;
    }

    const suffix = updatedOverdueDeliverables?.listById.length > 1 ? 'overdue_tasks' : 'overdue_task';

    const content = (
      <div className="subcampaign-banners__banner subcampaign-banners__overdue">
        {this.renderIcon('red')}
        {__t(`${MSG_KEY}.${suffix}`, { count: updatedOverdueDeliverables?.listById.length })}
      </div>
    );

    return this.renderBanner(`${this.getRoute()}#deliverables`, content, 'overdue');
  }

  renderOfferBanner() {
    const { subcampaign, campaign } = this.props;
    if (!isInfluencerActionRequired(subcampaign, campaign)) {
      return null;
    }

    const isNegotiationsReopenedForSubcampaign = isNegotiationsReopened(subcampaign);
    let offerSuffix = isNegotiationsReopenedForSubcampaign ? 'offer_negotiations_reopened' : 'offer_pending';

    if (isQuoteProvided(subcampaign)) {
      offerSuffix = 'offer_quote_provided';
    } else if (isQuote(subcampaign)) {
      offerSuffix = 'offer_quote';
    }

    const content = (
      <div className="subcampaign-banners__banner subcampaign-banners__offer">
        {this.renderIcon('orange')}
        {__t(`${MSG_KEY}.${offerSuffix}`)}
      </div>
    );

    return this.renderBanner(this.getRoute(), content, 'offer');
  }

  render() {
    return (
      <div className="subcampaign-banners">
        {this.renderOfferBanner()}
        {this.renderOverdueBanner()}
      </div>
    );
  }
}

export default SubcampaignBanners;
